/**
 * <s-product-gallery> — one-image-at-a-time product gallery with prev/next arrows.
 *
 * Arrow buttons use commandfor="<this id>" with command="--prev" / "--next".
 * Native snap scrolling (swipe, trackpad) keeps working; this component only
 * scrolls the track one slide per command and keeps the arrows and counter in sync.
 *
 * Required markup:
 *   [data-gallery-track]   — horizontal snap-scrolling container.
 *   [data-gallery-slide]   — each slide, a direct child of the track.
 * Optional:
 *   [data-gallery-current] — element whose text shows the current slide number.
 *   button[command="--prev"], button[command="--next"] — arrow controls.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll(':scope > [data-gallery-slide]')];
    if (this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.#go(this.#index - 1);
      else if (event.command === '--next') this.#go(this.#index + 1);
    }, opts);

    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });

    this.#track.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight') this.#go(this.#index + (rtl ? -1 : 1));
      else if (event.key === 'ArrowLeft') this.#go(this.#index + (rtl ? 1 : -1));
      else return;
      event.preventDefault();
    }, opts);

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #go(target) {
    const last = this.#slides.length - 1;
    // Wrap around so the arrows always do something.
    const next = target < 0 ? last : target > last ? 0 : target;
    // Every slide is one track-width wide; RTL scrolls toward negative offsets.
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: next * this.#track.clientWidth * (rtl ? -1 : 1),
      behavior: reduce ? 'auto' : 'smooth',
    });
    this.#setIndex(next);
  }

  #sync() {
    const width = this.#track.clientWidth || 1;
    const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
    this.#setIndex(Math.min(Math.max(index, 0), this.#slides.length - 1));
  }

  #setIndex(index) {
    this.#index = index;
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(index + 1);
    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== index);
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
